import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { createLLMOMeta, createBreadcrumbSchema, createToolSchema, createHowToSchema, createFAQSchema } from "@/lib/seo"
import { getToolBySlug } from "@/data/tools/tools-registry"
import ToolPageLayout from "@/components/tools/ToolPageLayout"
import { TextField, SubmitButton } from "@/components/tools/ToolFormField"
import { SectionHeading, Paragraph, FeatureGrid, TipList, ContentSection } from "@/components/tools/SeoContent"

const tool = getToolBySlug("instagram-utm-link-generator")!

export const Route = createFileRoute("/tools/instagram-utm-link-generator")({
  head: () => ({
    meta: [
      ...createLLMOMeta({ title: tool.seo.title, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-utm-link-generator" }),
      { name: "keywords", content: tool.seo.keywords.join(", ") },
    ],
    links: [{ rel: "canonical", href: "https://zapify.pro/tools/instagram-utm-link-generator" }],
    scripts: [
      createToolSchema({ name: tool.name, description: tool.seo.description, url: "https://zapify.pro/tools/instagram-utm-link-generator" }),
      createBreadcrumbSchema({ itemListElement: [
        { position: 1, name: "Home", item: "https://zapify.pro" },
        { position: 2, name: "Free Tools", item: "https://zapify.pro/tools" },
        { position: 3, name: tool.shortName, item: "https://zapify.pro/tools/instagram-utm-link-generator" },
      ]}),
      createHowToSchema({ name: "How to Create UTM Tracking Links", description: tool.seo.description, steps: tool.howToSteps }),
      createFAQSchema(tool.faq),
    ],
  }),
  component: UTMLinkGeneratorPage,
})

function UTMLinkGeneratorPage() {
  const [url, setUrl] = useState("")
  const [source, setSource] = useState("")
  const [medium, setMedium] = useState("")
  const [campaign, setCampaign] = useState("")
  const [term, setTerm] = useState("")
  const [content, setContent] = useState("")
  const [generatedUrl, setGeneratedUrl] = useState("")
  const [copied, setCopied] = useState(false)

  const generate = () => {
    if (!url || !source || !medium || !campaign) return

    let baseUrl = url.trim()
    if (!baseUrl.startsWith("http://") && !baseUrl.startsWith("https://")) {
      baseUrl = `https://${baseUrl}`
    }

    const params = new URLSearchParams()
    params.set("utm_source", source.toLowerCase().trim().replace(/\s+/g, "-"))
    params.set("utm_medium", medium.toLowerCase().trim().replace(/\s+/g, "-"))
    params.set("utm_campaign", campaign.toLowerCase().trim().replace(/\s+/g, "-"))
    if (term.trim()) params.set("utm_term", term.toLowerCase().trim().replace(/\s+/g, "-"))
    if (content.trim()) params.set("utm_content", content.toLowerCase().trim().replace(/\s+/g, "-"))

    const separator = baseUrl.includes("?") ? "&" : "?"
    setGeneratedUrl(`${baseUrl}${separator}${params.toString()}`)
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback
      const input = document.createElement("input")
      input.value = generatedUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand("copy")
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const reset = () => {
    setUrl("")
    setSource("")
    setMedium("")
    setCampaign("")
    setTerm("")
    setContent("")
    setGeneratedUrl("")
  }

  return (
    <ToolPageLayout
      tool={tool}
      content={
        <div className="space-y-10">
          <ContentSection>
            <SectionHeading>What Are UTM Parameters?</SectionHeading>
            <Paragraph>
              UTM (Urchin Tracking Module) parameters are small tags appended to the end of a URL that tell your analytics platform exactly where a visitor came from, how they got there, and which campaign drove the click. They were originally developed by Urchin Software, which Google acquired to create Google Analytics, and they remain the universal standard for campaign tracking across every major analytics tool.
            </Paragraph>
            <Paragraph className="mt-3">
              There are five UTM parameters, three required and two optional. Each serves a specific purpose in breaking down your traffic sources into actionable segments.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { icon: "\uD83C\uDF10", title: "utm_source (required)", description: "Identifies the platform or website sending the traffic. For Instagram campaigns, this is typically set to \"instagram.\" For other channels it might be \"facebook,\" \"google,\" \"newsletter,\" or the name of a specific referring website." },
              { icon: "\uD83D\uDCE1", title: "utm_medium (required)", description: "Describes the marketing channel or type of traffic. Common values include \"social\" for organic social media posts, \"paid-social\" for sponsored content, \"email\" for newsletter links, and \"cpc\" for paid search." },
              { icon: "\uD83C\uDFAF", title: "utm_campaign (required)", description: "Names the specific campaign or promotion. Examples include \"spring-sale,\" \"product-launch-2025,\" or \"black-friday.\" This is where you label the effort so you can isolate its results in your analytics dashboard." },
              { icon: "\uD83D\uDD0D", title: "utm_term (optional)", description: "Originally designed to track paid search keywords, but creators often repurpose it to tag specific topics, hashtags, or audience segments. For example, you might set utm_term to \"skincare-routine\" to track which content topics drive the most clicks." },
              { icon: "\uD83D\uDCCB", title: "utm_content (optional)", description: "Differentiates between multiple links within the same campaign. If you are running the same promotion in your bio and in a Story, you would set utm_content to \"bio-link\" for one and \"story-swipe\" for the other. Essential for A/B testing." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Why UTM Tracking Matters for Instagram</SectionHeading>
            <Paragraph>
              Instagram presents a unique tracking challenge that makes UTM parameters not just useful but essential. Unlike websites where referral headers are passed automatically, Instagram traffic often appears as "direct" or "not set" in Google Analytics because the Instagram app strips referral information when users click links. This means that without UTM tags, a click from your Instagram bio, a Story swipe-up, and a DM link all look identical in your analytics — they are all lumped into the same bucket of unattributed traffic.
            </Paragraph>
            <Paragraph className="mt-3">
              For creators and businesses that drive significant traffic from Instagram, this blind spot is costly. You cannot optimize what you cannot measure. If you are running a product launch and promoting it through your bio link, three Story frames, and a DM automation sequence, UTM parameters let you see exactly which touchpoint drove the most clicks, sign-ups, and sales. Without them, you are guessing — and in a world where brand deals, affiliate commissions, and product sales depend on proving ROI, guessing is not good enough.
            </Paragraph>
            <Paragraph className="mt-3">
              UTM tracking also helps you demonstrate value to brand partners. When you can show a brand that your Instagram Story generated 2,400 tracked clicks to their landing page with a 4.2% conversion rate, you have a data point that justifies your rate and positions you for repeat partnerships. Creators who track their campaigns with UTMs consistently negotiate higher rates because they can prove measurable business impact.
            </Paragraph>
          </ContentSection>

          <ContentSection>
            <SectionHeading>UTM Best Practices</SectionHeading>
            <Paragraph>
              UTM parameters are only as useful as they are consistent. Sloppy tagging creates messy analytics that are harder to interpret than no tagging at all. Follow these best practices to keep your data clean and actionable.
            </Paragraph>
            <TipList tips={[
              { title: "Use lowercase for everything", description: "UTM parameters are case-sensitive in most analytics platforms. \"Instagram\" and \"instagram\" will appear as two separate sources. Always use lowercase to avoid fragmenting your data. This generator automatically converts your inputs to lowercase, but if you create UTM links manually elsewhere, make this a non-negotiable rule." },
              { title: "Use hyphens instead of spaces or underscores", description: "Spaces in URLs get encoded as \"%20\" which makes links ugly and harder to read. Underscores work technically but hyphens are the web standard and are easier to read in analytics reports. Use \"spring-sale\" instead of \"spring_sale\" or \"spring sale\" for clean, professional tracking links." },
              { title: "Be consistent with naming conventions", description: "Decide on your naming scheme before you start and document it. If your source is \"instagram\" for one campaign, do not switch to \"ig\" or \"insta\" for the next. Inconsistency is the number one cause of messy UTM data. Create a simple spreadsheet or document listing your standard values for source, medium, and campaign naming patterns." },
              { title: "Never include personally identifiable information (PII)", description: "Do not put email addresses, names, phone numbers, or any personal data in UTM parameters. These values appear in your analytics reports, can be stored in server logs, and may violate privacy regulations like GDPR and CCPA. Use anonymized identifiers or segment names instead." },
              { title: "Keep campaign names descriptive but concise", description: "Your future self needs to understand what \"q1-2025-skincare-launch\" means six months from now. Avoid cryptic abbreviations like \"q1sk25\" that require a decoder ring. At the same time, do not make names so long they become unwieldy. Aim for three to four hyphenated words that clearly identify the campaign purpose and timing." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>Where to Use UTM Links on Instagram</SectionHeading>
            <Paragraph>
              Instagram offers several link placement opportunities, and each one should have its own UTM-tagged URL so you can track performance independently. Here are the four key placements and how to tag each one.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { icon: "\uD83D\uDD17", title: "Bio Link", description: "Your bio link is the most persistent link on your profile and typically receives the most cumulative clicks over time. Tag it with utm_content set to \"bio-link\" so you can isolate its performance from other placements. If you use a link-in-bio tool, tag each individual destination URL separately. Update the campaign tag whenever you change the link destination." },
              { icon: "\uD83D\uDCF8", title: "Stories", description: "Story link stickers are one of the highest-converting placements on Instagram because they pair a visual call-to-action with a single tap. Tag each Story link with utm_content set to something specific like \"story-frame-1\" or \"story-swipe-march\" so you can compare performance across different Story posts." },
              { icon: "\uD83D\uDCAC", title: "DMs (Direct Messages)", description: "Links shared through DM automations or manual messages should always be UTM-tagged with utm_content set to \"dm-auto\" or \"dm-manual.\" DM links tend to have high conversion rates because they are delivered in a one-to-one context that feels personal. If you use Zapify for DM automations, tagging each automated link with UTMs gives you precise conversion data." },
              { icon: "\uD83D\uDCE2", title: "Instagram Ads", description: "Adding UTM parameters to your ad destination URLs gives you a parallel data source in Google Analytics alongside Meta Pixel tracking. Set utm_medium to \"paid-social\" to differentiate ad traffic from organic social traffic, and include the ad set or creative name in utm_content for granular performance tracking." },
            ]} />
          </ContentSection>

          <ContentSection>
            <SectionHeading>UTM Parameters Explained</SectionHeading>
            <Paragraph>
              Here is a detailed breakdown of each parameter with practical examples for Instagram creators and marketers.
            </Paragraph>
            <FeatureGrid columns={2} items={[
              { title: "utm_source", description: "Identifies the traffic source. For Instagram, always use \"instagram\". For other platforms: \"facebook\", \"tiktok\", \"newsletter\". This is the broadest level of attribution and answers \"where did this visitor come from?\"" },
              { title: "utm_medium", description: "Categorizes the channel type. Use \"social\" for organic posts, \"paid-social\" for ads, \"email\" for newsletters, \"referral\" for partner links. This groups your traffic into comparable marketing channels." },
              { title: "utm_campaign", description: "Names the specific promotion or effort. Examples: \"spring-sale\", \"product-launch-march\", \"brand-collab-acme\". Choose names that will still make sense to you months later when reviewing historical data." },
              { title: "utm_term", description: "Tags keywords or topics. Originally for paid search, creators use it for content topics: \"skincare-routine\", \"budget-travel\". This helps you identify which content themes drive the most qualified traffic." },
              { title: "utm_content", description: "Differentiates link placements within one campaign. Examples: \"bio-link\", \"story-swipe\", \"dm-auto\", \"carousel-slide-3\". Essential for A/B testing which placement converts best." },
            ]} />
          </ContentSection>
        </div>
      }
    >
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
          {!generatedUrl ? (
            <div className="space-y-4">
              <TextField label="Website URL" value={url} onChange={setUrl} placeholder="https://yoursite.com/page" required type="url" />
              <div className="grid grid-cols-3 gap-3">
                <TextField label="Source" value={source} onChange={setSource} placeholder="instagram" required />
                <TextField label="Medium" value={medium} onChange={setMedium} placeholder="social" required />
                <TextField label="Campaign" value={campaign} onChange={setCampaign} placeholder="spring-sale" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <TextField label="Term" value={term} onChange={setTerm} placeholder="keyword" hint="optional" />
                <TextField label="Content" value={content} onChange={setContent} placeholder="bio-link" hint="optional" />
              </div>
              <SubmitButton onClick={generate} disabled={!url || !source || !medium || !campaign}>
                Generate UTM Link
              </SubmitButton>
            </div>
          ) : (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2">Your Tracking Link</p>
                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 p-4">
                  <p className="text-sm text-neutral-800 dark:text-neutral-200 break-all font-mono">{generatedUrl}</p>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={copyToClipboard}
                  className={`flex-1 h-12 rounded-xl font-semibold text-sm transition-all duration-300 ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-brand-accent text-white shadow-sm hover:shadow-md hover:-translate-y-0.5"
                  }`}>
                  {copied ? "Copied!" : "Copy Link"}
                </button>
                <button onClick={reset}
                  className="flex-1 h-12 rounded-xl border border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all">
                  Create Another
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ToolPageLayout>
  )
}
